/**
 * Unit Preferences Store
 * Manages user's preferred units for different measurement types
 * Integrates with backend API for persistence
 *
 * Until the user picks a unit explicitly (`useNationalityDefaults` is true —
 * the name is the backend's), the units follow the app language: English
 * shows US units (lb, ft/in, °F, mg/dL), every other language metric. An
 * explicit choice is stored and wins over the language.
 */

import { useMemo } from "react";
import { create } from "zustand";
import { getAuthUserId } from "../utils/authToken";
import { persist } from "zustand/middleware";
import api from "../services/api";
import type { Language } from "../types/i18n";
import { getUnitSystem, type QuantitySystems } from "../utils/unitLocalization";
import { useI18nStore } from "./i18nStore";

export type UnitType =
  | "weight"
  | "height"
  | "temperature"
  | "bloodGlucose"
  | "cholesterol";

export interface UnitPreferences {
  weight: "kg" | "[lb_av]" | "[stone_av]";
  height: "cm" | "[in_i]" | "[ft_i]";
  temperature: "Cel" | "[degF]";
  bloodGlucose: "mmol/L" | "mg/dL";
  cholesterol: "mmol/L" | "mg/dL";
}

export interface UserUnitPreferences {
  userId: string;
  useNationalityDefaults: boolean;
  customPreferences?: Partial<UnitPreferences>;
  lastUpdated: Date;
}

interface UnitPreferencesStore {
  // State
  preferences: UnitPreferences | null;
  useNationalityDefaults: boolean;
  loading: boolean;
  error: string | null;

  // Actions
  loadPreferences: () => Promise<void>;
  updatePreference: (unitType: UnitType, unit: string) => Promise<void>;
  resetToNationalityDefaults: () => Promise<void>;
  getPreferredUnit: (unitType: UnitType) => string;
  /** The units in effect: the explicit choice, or the app-language defaults. */
  getEffectivePreferences: () => UnitPreferences;

  // Utility
  getUnitDisplay: (ucumCode: string) => string;
  convertValue: (
    value: number,
    fromUnit: string,
    toUnit: string,
  ) => Promise<number | null>;
}

const METRIC_PREFERENCES: UnitPreferences = {
  weight: "kg",
  height: "cm",
  temperature: "Cel",
  bloodGlucose: "mmol/L",
  cholesterol: "mmol/L",
};

const US_PREFERENCES: UnitPreferences = {
  weight: "[lb_av]",
  height: "[ft_i]",
  temperature: "[degF]",
  bloodGlucose: "mg/dL",
  cholesterol: "mg/dL",
};

/**
 * Each unit as a factor to its stored base (kg, cm). The inputs used to send
 * every keystroke to /unit-conversion/convert; offline that returned null and
 * the value the user typed was never saved.
 */
const LINEAR_FACTORS: Record<string, { base: string; factor: number }> = {
  kg: { base: "kg", factor: 1 },
  "[lb_av]": { base: "kg", factor: 0.45359237 },
  "[stone_av]": { base: "kg", factor: 6.35029318 },
  cm: { base: "cm", factor: 1 },
  "[in_i]": { base: "cm", factor: 2.54 },
  "[ft_i]": { base: "cm", factor: 30.48 },
};

/** Weight, height or temperature between two UCUM codes; null for anything else. */
export function convertLocally(
  value: number,
  fromUnit: string,
  toUnit: string,
): number | null {
  if (fromUnit === toUnit) return value;
  const temps: Record<string, true> = { Cel: true, "[degF]": true };
  if (temps[fromUnit] && temps[toUnit]) {
    return fromUnit === "Cel" ? (value * 9) / 5 + 32 : ((value - 32) * 5) / 9;
  }
  const from = LINEAR_FACTORS[fromUnit];
  const to = LINEAR_FACTORS[toUnit];
  if (!from || !to || from.base !== to.base) return null;
  return (value * from.factor) / to.factor;
}

/** Which display system each quantity is in, for the formatting helpers. */
export function systemsOf(prefs: UnitPreferences): QuantitySystems {
  return {
    weight: prefs.weight === "kg" ? "metric" : "imperial",
    height: prefs.height === "cm" ? "metric" : "imperial",
    temperature: prefs.temperature === "Cel" ? "metric" : "imperial",
  };
}

/** The units a language implies when the user has not chosen any. */
export function languageDefaultPreferences(
  language: Language,
): UnitPreferences {
  return getUnitSystem(language) === "imperial"
    ? US_PREFERENCES
    : METRIC_PREFERENCES;
}

/**
 * The one rule for the units in effect: an explicit choice wins, otherwise the
 * language decides. The store and the body-data chart both read it, so they
 * cannot drift apart.
 */
export function effectivePreferences(
  language: Language,
  useNationalityDefaults: boolean | undefined,
  preferences: Partial<UnitPreferences> | null | undefined,
): UnitPreferences {
  if (useNationalityDefaults !== false || !preferences) {
    return languageDefaultPreferences(language);
  }
  return { ...languageDefaultPreferences(language), ...preferences };
}

export const useUnitPreferencesStore = create<UnitPreferencesStore>()(
  persist(
    (set, get) => ({
      // Initial State
      preferences: null,
      useNationalityDefaults: true,
      loading: false,
      error: null,

      // Load user preferences from API
      loadPreferences: async () => {
        const userId = getAuthUserId();
        if (!userId) return;

        set({ loading: true, error: null });

        try {
          const response = await api.get(`/unit-preferences/${userId}`);

          if (response.data.success) {
            const prefs = response.data.data;
            const followsLanguage =
              prefs.useNationalityDefaults !== false ||
              !prefs.customPreferences;
            set({
              // Defaults the server derived are not kept: without an
              // explicit choice the units follow the app language.
              preferences: followsLanguage
                ? null
                : { ...METRIC_PREFERENCES, ...prefs.customPreferences },
              useNationalityDefaults: followsLanguage,
              loading: false,
            });
          }
        } catch (error: any) {
          console.error("Failed to load unit preferences:", error);
          // Follow the app language on error
          set({
            preferences: null,
            useNationalityDefaults: true,
            loading: false,
            error: error.message,
          });
        }
      },

      // Update a single preference
      updatePreference: async (unitType: UnitType, unit: string) => {
        const currentPrefs = get().getEffectivePreferences();
        const updatedPrefs = { ...currentPrefs, [unitType]: unit };

        set({ loading: true, error: null });

        try {
          const userId = getAuthUserId();

          if (!userId) {
            throw new Error("User not authenticated");
          }

          const response = await api.put(`/unit-preferences/${userId}`, {
            useNationalityDefaults: false,
            customPreferences: updatedPrefs,
          });

          if (response.data.success) {
            set({
              preferences: updatedPrefs,
              useNationalityDefaults: false,
              loading: false,
            });
          }
        } catch (error: any) {
          console.error("Failed to update preference:", error);
          set({ loading: false, error: error.message });
          throw error;
        }
      },

      // Back to the app-language defaults (the backend calls it nationality)
      resetToNationalityDefaults: async () => {
        const userId = getAuthUserId();
        if (!userId) throw new Error("User not authenticated");

        set({ loading: true, error: null });

        try {
          const response = await api.post(`/unit-preferences/${userId}/reset`);

          if (response.data.success) {
            set({
              preferences: null,
              useNationalityDefaults: true,
              loading: false,
            });
          }
        } catch (error: any) {
          console.error("Failed to reset preferences:", error);
          set({ loading: false, error: error.message });
          throw error;
        }
      },

      // Get preferred unit for a measurement type
      getPreferredUnit: (unitType: UnitType): string =>
        get().getEffectivePreferences()[unitType],

      getEffectivePreferences: (): UnitPreferences => {
        const { preferences, useNationalityDefaults } = get();
        return effectivePreferences(
          useI18nStore.getState().language,
          useNationalityDefaults,
          preferences,
        );
      },

      // Get human-readable unit display
      getUnitDisplay: (ucumCode: string): string => {
        const displayMap: Record<string, string> = {
          kg: "kg",
          "[lb_av]": "lb",
          "[stone_av]": "st",
          cm: "cm",
          "[in_i]": "in",
          "[ft_i]": "ft",
          Cel: "°C",
          "[degF]": "°F",
          "mmol/L": "mmol/L",
          "mg/dL": "mg/dL",
          "mm[Hg]": "mmHg",
          "/min": "bpm",
          "%": "%",
          "kg/m2": "kg/m²",
        };

        return displayMap[ucumCode] || ucumCode;
      },

      // Weight, height and temperature are converted here, exactly and
      // offline. Only a pair this file does not know goes to the server.
      convertValue: async (
        value: number,
        fromUnit: string,
        toUnit: string,
      ): Promise<number | null> => {
        if (fromUnit === toUnit) return value;
        const local = convertLocally(value, fromUnit, toUnit);
        if (local !== null) return local;

        try {
          const response = await api.post("/unit-conversion/convert", {
            value,
            fromUnit,
            toUnit,
          });

          if (response.data.success) {
            return response.data.data.converted.value;
          }
          return null;
        } catch (error: any) {
          console.error("Conversion failed:", error);
          return null;
        }
      },
    }),
    {
      name: "unit-preferences-storage",
      partialize: (state) => ({
        preferences: state.preferences,
        useNationalityDefaults: state.useNationalityDefaults,
      }),
    },
  ),
);

/**
 * The per-quantity systems the user chose explicitly, or undefined while the
 * units follow the language. Stable between renders unless the choice changes,
 * so it can sit in a hook's dependency list.
 */
export function useChosenSystems(): QuantitySystems | undefined {
  const preferences = useUnitPreferencesStore((st) => st.preferences);
  const followsLanguage = useUnitPreferencesStore(
    (st) => st.useNationalityDefaults,
  );
  return useMemo(
    () =>
      followsLanguage || !preferences ? undefined : systemsOf(preferences),
    [followsLanguage, preferences],
  );
}
